import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  DefaultStylePanel,
  type Editor,
  type TLPresenceUserInfo,
  type TLStore,
} from "tldraw";
import { useAuthStore } from "@/store/useAuthStore";
import SpaceAgentPanel from "../components/SpaceAgentPanel";
import SpaceAgentOverlay from "../components/SpaceAgentOverlay";
import SpaceBottomBar from "../components/SpaceBottomBar";
import { useSpaceAgent } from "../hooks/useSpaceAgent";
import { useSpaceMic } from "../hooks/useSpaceMic";
import Captions from "@/features/tutor/components/Captions";
import { useCaptionsQueue } from "@/features/tutor/hooks/useCaptionsQueue";
import { RuntimeProvider } from "@/features/tutor/context/RuntimeContext";
import { useUIStore } from "@/features/tutor/store/useUIStore";
import { useStudentSpaceCanvases } from "../hooks/useStudentSpaceCanvases";
import { useStudentSpaceSession } from "../hooks/useStudentSpaceSession";
import { useStudentCameraStream } from "../hooks/useStudentCameraStream";
import { useHoldToTalk } from "../hooks/useHoldToTalk";
import { useTldrawDrawLock } from "@/drawlock/useTldrawDrawLock";
import { SPACES_STUDENT_SURFACE_ID } from "@/drawlock/surfaceIds";
import { useConnectionStore } from "@/features/tutor/store/useConnectionStore";
import SystemToasts from "@/features/tutor/components/connection/SystemToasts";
import { useSpaceStore } from "../store/useSpaceStore";
import { useViewport } from "@/hooks/useViewport";
import { useForm } from "@/features/tutor/context/FormContext";
import { getSessionVoiceConfig } from "@/constants/audioVoice";
import { ClosedCanvas } from "../components/ClosedCanvas";
import { SpacePausedModal } from "../components/SpacePausedModal";
import { CanvasStylePanel } from "@/canvas/components/CanvasPanels";
import {
  StudentPersonalSyncBridge,
  StudentSharedCanvas,
} from "../components/studentSpaceCanvasComponents";
import { getAvatarColor } from "../spacesPresenceUtils";
import { useSpacesEventStream } from "../hooks/spacesEventStreamHook";
import type { SpaceCardUpdatedPayload } from "../hooks/spacesSSEEventPayloadTypes";
import { useSyncMultiplexer } from "../hooks/useSyncMultiplexer";
import "@/canvas/canvas.css";
import "../space-live.css";

export default function StudentSpacePage() {
  // RuntimeProvider must wrap the page body so useSpaceAgent and
  // useCaptionsQueue (both consumers of useRuntimeContext) can resolve.
  return (
    <RuntimeProvider>
      <StudentSpacePageInner />
    </RuntimeProvider>
  );
}

function StudentSpacePageInner() {
  const { spaceId } = useParams<{ spaceId: string }>();
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const aiMode = useSpaceStore((s) => s.aiMode);
  const collabMode = useSpaceStore((s) => s.collabMode);
  const { isMobilePortrait, isMobileLandscape, isTabletPortrait } =
    useViewport();
  const [isAgentOverlayOpen, setIsAgentOverlayOpen] = useState(false);

  const userInfo = useMemo<TLPresenceUserInfo | undefined>(() => {
    if (!user) return undefined;
    const displayName = user.name || user.username || "";
    return {
      id: String(user.id),
      name: displayName,
      color: getAvatarColor(user.id).text,
    };
  }, [user]);

  // Editor is set by useStudentSpaceCanvases below; session-hook reads it
  // lazily through this ref so we can run the session hook before the canvas
  // hook (the session needs writeMode+closed state, the canvas needs the
  // editor mount handler — passing the editor ref breaks the cycle).
  const editorGetterRef = useRef<() => Editor | null>(() => null);
  const getEditorLazy = useCallback(() => editorGetterRef.current(), []);

  const {
    spaceInfo,
    patchSpaceInfo,
    joinComplete,
    writeMode,
    spaceClosed,
    closedFromStart,
    closedEditorSnapshot,
    spacePaused,
    savedCamera,
    sendCtrl,
  } = useStudentSpaceSession({ spaceId, token, getEditor: getEditorLazy });

  const handleSpaceCardUpdated = useCallback(
    ({ spaceId: eventSpaceId, spaceName }: SpaceCardUpdatedPayload) => {
      if (eventSpaceId !== spaceId) return;
      patchSpaceInfo({ spaceName });
    },
    [spaceId, patchSpaceInfo],
  );

  useSpacesEventStream(joinComplete, {
    onSpaceCardUpdated: handleSpaceCardUpdated,
  });

  const spacesBase = import.meta.env.VITE_SPACES_BASE_URL as string;

  // Multiplexed sync WS — created once when join completes and space is active
  const syncUri =
    spaceId && token && joinComplete && !closedFromStart && !spaceClosed
      ? `${spacesBase.replace(/^http/, "ws")}/spaces/sync/${spaceId}?token=${token}`
      : null;

  const mux = useSyncMultiplexer(syncUri);

  // Reflect mux WS state into the global connection store so SystemToasts
  // can show the same "unstable" / "back online" banner used in the tutor.
  useEffect(() => {
    if (!mux) return;
    return mux.onConnectionChange((isConnected) => {
      const store = useConnectionStore.getState();
      if (isConnected) store.markConnected();
      else store.markDisconnected();
    });
  }, [mux]);

  // Mux generation — bumped on each unexpected disconnect. Used as a React `key`
  // on canvas + bridge so useSync fully remounts after a reconnect, getting a
  // fresh tldraw client + clean handshake (otherwise a DO restart would leave
  // the client trying to resume a session the server no longer knows about,
  // surfacing as tldraw's "Something went wrong" error).
  const [muxGeneration, setMuxGeneration] = useState(0);
  useEffect(() => {
    if (!mux) return;
    return mux.onGenerationChange(setMuxGeneration);
  }, [mux]);

  // Personal sync store — mounted whenever we have a mux and a userId,
  // regardless of writeMode/collabMode. The personal channel is the AI's
  // permanent destination: even in read-mode (where the student can't draw)
  // or shared-mode (where student writes go to the shared room), the AI
  // still mirrors its shapes here so the DO and any teacher in Student Work
  // see them. The student-facing canvas is gated separately by writeMode in
  // useStudentSpaceCanvases.
  const [personalSyncStore, setPersonalSyncStore] = useState<TLStore | null>(
    null,
  );
  const personalSyncEnabled = !!mux && spaceInfo?.userId !== undefined;
  const personalSyncUserId = String(spaceInfo?.userId ?? "");

  const {
    onWhiteboardMount: onWhiteboardMountInner,
    getEditor,
    getCanvasContext,
    getCanvasScreenshot,
  } = useStudentSpaceCanvases(
    spaceInfo?.userId,
    spaceInfo?.isTeacher,
    writeMode,
    collabMode,
    spaceClosed || spacePaused,
    savedCamera,
    personalSyncStore,
  );
  // Wire the real editor getter into the ref the session hook reads from.
  useEffect(() => {
    editorGetterRef.current = getEditor;
  }, [getEditor]);

  const [editorReady, setEditorReady] = useState(false);
  const onWhiteboardMount = useCallback(
    (editor: Editor) => {
      onWhiteboardMountInner(editor);
      setEditorReady(true);
    },
    [onWhiteboardMountInner],
  );

  // Read the student's live selection (hydrated from localStorage) rather than
  // the static default, so the space honors their chosen voice/language.
  // Gender is not sent to the gateway (it has no voice_gender field and infers
  // gender from voice_id), so it's deliberately not derived here.
  const { formData } = useForm();
  const { voiceId: studentVoiceId, ttsProvider: studentVoiceModel } =
    getSessionVoiceConfig(formData.voice.voiceId);
  // useSpaceAgent sends voiceModel and voiceProvider to the gateway as two
  // separate WS query params. They carry the same TTS provider value today —
  // voiceModel is kept for the gateway's existing contract, voiceProvider is
  // the field it can migrate to later — hence the distinct name here instead
  // of passing studentVoiceModel to both positions below.
  const studentVoiceProvider = studentVoiceModel;
  const studentLanguage = formData.language?.value ?? "en";

  // Stable accessor over personalSyncStore so useSpaceAgent reads the
  // current value at action-time without re-running its WS effect.
  const personalSyncStoreRef = useRef<TLStore | null>(null);
  personalSyncStoreRef.current = personalSyncStore;
  const getPersonalSyncStore = useCallback(
    () => personalSyncStoreRef.current,
    [],
  );

  const {
    messages,
    agentPhase,
    sendMessage,
    startVoiceTurn,
    sendAudio,
    finalizeAudio,
    isWsOpen,
  } = useSpaceAgent(
    spaceId ?? "",
    spaceInfo?.lessonContext ?? null,
    getCanvasContext,
    getCanvasScreenshot,
    getEditor,
    studentVoiceModel,
    studentVoiceId,
    studentVoiceProvider,
    studentLanguage,
    aiMode !== "off" && !!spaceId && joinComplete,
    spaceInfo?.userId ?? null,
    getPersonalSyncStore,
  );

  const {
    startRecording,
    stopRecording,
    armRecording,
    abortArmedRecording,
    isRecording,
    isArming,
  } = useSpaceMic({
    enabled: aiMode === "voice" && isWsOpen,
    onChunk: sendAudio,
  });
  // Armed = key down, buffering locally, turn not open yet — shows the same
  // "listening" UI so the press feels instant.
  const micActive = isRecording || isArming;

  // Open captions whenever voice mode is active (mirrors prior space-only hook UX).
  const setCaptionsOpen = useUIStore((s) => s.setCaptionsOpen);
  useEffect(() => {
    if (aiMode === "voice") setCaptionsOpen(true);
  }, [aiMode, setCaptionsOpen]);
  useCaptionsQueue();

  const handleMicDown = useCallback(() => {
    startVoiceTurn();
    // startVoiceTurn has already told the gateway to listen, so a capture that
    // never starts has to be finalized or that turn hangs.
    void startRecording().then((started) => {
      if (!started) finalizeAudio();
    });
  }, [startVoiceTurn, startRecording, finalizeAudio]);
  const handleMicUp = useCallback(() => {
    stopRecording().then(finalizeAudio);
  }, [stopRecording, finalizeAudio]);
  const handleToggleAgentPanel = useCallback(
    () => setIsAgentOverlayOpen((o) => !o),
    [],
  );

  // Space bar locks the pen down for trackpad handwriting. Stood down unless
  // the student actually has write access to a live, joined space — a
  // read-only, paused or closed space must never accept synthetic ink.
  useTldrawDrawLock({
    surfaceId: SPACES_STUDENT_SURFACE_ID,
    getEditor: getEditorLazy,
    isActive:
      editorReady &&
      joinComplete &&
      writeMode &&
      !spaceClosed &&
      !closedFromStart &&
      !spacePaused,
  });

  useHoldToTalk({
    enabled: aiMode === "voice",
    startVoiceTurn,
    startRecording,
    stopRecording,
    finalizeAudio,
    armRecording,
    abortArmedRecording,
  });

  useStudentCameraStream({
    enabled: joinComplete && !spaceClosed && !closedFromStart && editorReady,
    getEditor,
    sendCtrl,
  });

  return (
    <div
      className={`flex h-[100dvh] overflow-hidden et-space-frame font-mulish ${isMobileLandscape ? "flex-row" : "flex-col"}`}
    >
      <SystemToasts />
      {personalSyncEnabled && mux && (
        <StudentPersonalSyncBridge
          key={`${personalSyncUserId}-gen${muxGeneration}`}
          mux={mux}
          userId={personalSyncUserId}
          userInfo={userInfo}
          onStoreChange={setPersonalSyncStore}
        />
      )}
      <div className="flex flex-1 min-h-0">
        <div
          className="flex-1 relative"
          style={
            isMobileLandscape ? { margin: 12 } : { margin: 12, marginBottom: 0 }
          }
        >
          {agentPhase === "loading" && (
            <div className="spaces-loading-border absolute -inset-[4px] rounded-[15px] pointer-events-none z-[21]" />
          )}
          {collabMode === "shared" && !spaceClosed && (
            <div className="spaces-collab-border absolute -inset-[3px] rounded-[14px] pointer-events-none z-[20]" />
          )}
          <div
            id="main-screen"
            className="absolute inset-0 rounded-[11px] overflow-hidden shadow-lg"
            style={{ background: "var(--tl-color-background)" }}
          >
            {closedFromStart && joinComplete ? (
              <ClosedCanvas
                snapshot={spaceInfo?.initialSnapshot}
                personalShapes={spaceInfo?.personalShapes ?? []}
                camera={savedCamera}
              />
            ) : spaceClosed && closedEditorSnapshot ? (
              <ClosedCanvas
                snapshot={closedEditorSnapshot}
                personalShapes={[]}
                camera={savedCamera}
              />
            ) : mux ? (
              <StudentSharedCanvas
                key={`shared-gen${muxGeneration}`}
                mux={mux}
                userInfo={userInfo}
                onMount={onWhiteboardMount}
                stylePanel={
                  isMobilePortrait || isMobileLandscape || isTabletPortrait
                    ? DefaultStylePanel
                    : CanvasStylePanel
                }
              />
            ) : null}
            {spaceClosed && (
              <div className="et-space-closed-strip absolute inset-x-0 top-0 z-10 flex items-center justify-center gap-2 py-2 text-sm font-semibold pointer-events-none">
                Space closed — view only
              </div>
            )}
            <SpacePausedModal isVisible={spacePaused && !spaceClosed} />
            {aiMode === "text-only" &&
              (isMobilePortrait || isMobileLandscape) && (
                <SpaceAgentOverlay
                  isOpen={isAgentOverlayOpen}
                  messages={messages}
                  agentPhase={agentPhase}
                  isRecording={isRecording}
                  sendMessage={sendMessage}
                />
              )}
          </div>
        </div>

        {aiMode === "text-only" && !isMobilePortrait && !isMobileLandscape && (
          <SpaceAgentPanel
            messages={messages}
            agentPhase={agentPhase}
            isRecording={isRecording}
            sendMessage={sendMessage}
          />
        )}
        {aiMode === "voice" && <Captions />}
      </div>

      <SpaceBottomBar
        spaceName={spaceInfo?.spaceName ?? ""}
        teacherName={spaceInfo?.teacherName ?? null}
        voiceEnabled={aiMode === "voice"}
        isWsOpen={isWsOpen}
        isRecording={micActive}
        onMicDown={handleMicDown}
        onMicUp={handleMicUp}
        spaceClosed={spaceClosed}
        agentPanelEnabled={
          aiMode === "text-only" && (isMobilePortrait || isMobileLandscape)
        }
        isAgentPanelOpen={isAgentOverlayOpen}
        onToggleAgentPanel={handleToggleAgentPanel}
      />
    </div>
  );
}
